// Reference data: what the server knows about a sport, and which contract it
// is serving.
//
// WHY THESE ARE ENDPOINTS AT ALL. The setup wizard rendered its scoring
// presets, its formats and its preview sentences by importing the football
// package into the browser and running `buildDefaultSettings` (175 lines) and
// `describeSettings` (470 lines) against it; `JoinScreen` resolved a pro team
// to two hex colors the same way. A client that is not TypeScript has two
// options there — show nothing, or port about 860 lines of rules logic and
// keep it byte-identical forever (audit 3.6). Serving it makes the server the
// one answer, and the browser app reads the same endpoint so the two cannot
// drift.
//
// The core still never names a sport: this is a projection OF a
// `SportDefinition`, built by the one composition root on the server that is
// allowed to import one.

import { z } from 'zod';
import { RulesDocument } from '../rules/describe';
import { ScoringQuestion, ScoringTable } from '../rules/scoring-table';
import { Issue } from '../rules/validate';
import { DraftMode, DraftType, LeagueSettings } from '../rules/schemas';

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** A team in the real-world league, and the two colors a client paints with. */
export const ProTeamRef = z.object({
  id: z.string(),
  name: z.string(),
  shortName: z.string(),
  abbreviation: z.string(),
  location: z.string(),
  conference: z.string().nullable(),
  division: z.string().nullable(),
  palette: z.object({
    primary: Hex,
    secondary: Hex,
    accent: Hex.nullable(),
    /** Text that passes contrast on `primary`. Already decided; never computed twice. */
    onPrimary: Hex,
  }),
});
export type ProTeamRef = z.infer<typeof ProTeamRef>;

export const PositionRef = z.object({
  id: z.string(),
  name: z.string(),
  /** 'offense', 'skater', … Grouping only, never logic. */
  group: z.string(),
  /** A design-token name, not a color: the theme decides what it looks like. */
  color: z.string(),
  sortOrder: z.number().int(),
  /**
   * How the position reads on screen: the name of the starting slot that
   * takes it alone, or its id where none does (`PositionNumbers.label`) —
   * football's `DST` reads *DEF*, as its slot does. Added 2026-10-26; absent,
   * the id.
   */
  label: z.string().optional(),
});
export type PositionRef = z.infer<typeof PositionRef>;

export const PresetRef = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
});
export type PresetRef = z.infer<typeof PresetRef>;

export const FormatRef = z.object({
  id: z.string(),
  /** "Head-to-head" — for a chip. */
  label: z.string(),
  /** "Head-to-head, most points wins each matchup": the rules page's sentence, which names it. */
  description: z.string(),
  /** "Play one team each week. The higher score wins." — the line under the name on a choice. Added 2026-10-18. */
  tagline: z.string(),
  /**
   * The format for somebody new to it, in labeled lines: *Each week*, *Who
   * wins*, *Good to know* (`formatWords`, B-429). For a new league, as the
   * create form makes one. Added 2026-10-18.
   */
  explainer: z.array(z.object({ label: z.string(), text: z.string() })),
});
export type FormatRef = z.infer<typeof FormatRef>;

/**
 * One sport, as far as a client that has to draw the setup wizard needs.
 *
 * Deliberately NOT the whole `SportDefinition`: no stat table, no slot
 * eligibility, no research modules. Those belong to scoring and to screens
 * that do not exist yet, and a reference endpoint that serves everything is
 * one nobody can cache.
 */
export const SportSummary = z.object({
  id: z.string(),
  name: z.string(),
  /** An emoji. Display only. */
  icon: z.string(),
  /** 'NFL'. Display only, never logic. */
  proLeagueName: z.string(),
  positions: z.array(PositionRef),
  scoringPresets: z.array(PresetRef),
  rosterPresets: z.array(PresetRef),
  formats: z.array(FormatRef),
  draftTypes: z.array(z.string()),
  /** `autopick`, `manual`, `live` (settings version 3). */
  draftModes: z.array(z.string()),
  proTeams: z.array(ProTeamRef),
  /**
   * How a lineup is scoped in this sport — `week`, `day`, … — and which one a
   * new league gets. "Week" is "period" everywhere else in this codebase; this
   * is the one place the sport says what a period IS.
   */
  lineupPeriods: z.array(z.string()),
  defaultLineupPeriod: z.string(),
  /**
   * The days a league's weekly waiver run may fall on, 0 for Sunday, in the
   * league's time zone (B-509): a save that sets another is refused at
   * `waivers.processing.weekday`, unless the league already ran on that day.
   * Football's are Wednesday through Saturday. Added 2026-10-25.
   */
  waiverWeekdays: z.array(z.number().int().min(0).max(6)),
  /**
   * How the sport measures a player's usage (E8), named as its stat catalog
   * names the stats, so a client labels `DeeperNumbers.usage` without knowing
   * the sport: `share`, the stat a player's share of his club's total is
   * taken in ("Targets"), and `perGame`, the one averaged a game beside it
   * ("Air Yards"). Null for a sport with none. Added 2026-10-03.
   */
  usage: z
    .object({
      share: z.object({ key: z.string(), name: z.string(), shortName: z.string() }),
      perGame: z.object({ key: z.string(), name: z.string(), shortName: z.string() }).nullable(),
    })
    .nullable()
    .default(null),
  /**
   * The create form's scoring questions (DIRECTION A12): a switch for
   * whether a catch scores, a choice of a team's defense or defensive
   * players. Each answer's sentence is what the form shows under it; an
   * option with `available: false` is shown greyed and refused if sent. Sent
   * back as `CreateLeagueRequest.scoringSetup`. Empty for a sport that asks
   * none, whose form offers `scoringPresets` by name. Added 2026-10-18.
   */
  scoringQuestions: z.array(ScoringQuestion).default([]),
});
export type SportSummary = z.infer<typeof SportSummary>;

export const SportsView = z.object({
  sports: z.array(SportSummary),
});
export type SportsView = z.infer<typeof SportsView>;

/** `GET /api/sports/:sport/league-names?count=` — how many ideas, 1 to 50, 12 when absent. */
export const LeagueNameIdeasQuery = z.object({
  count: z.coerce.number().int().min(1).max(50).default(12),
});
export type LeagueNameIdeasQuery = z.infer<typeof LeagueNameIdeasQuery>;

/**
 * League name ideas for the create form's *Suggest one* (B-430): `count`
 * different names, each sharing no word with the one before it, from the
 * core's words and the sport's own (`leagueNameIdeas`). A new handful on
 * every request, so never cached. A client shows them one at a time, in
 * order, and asks again when it runs out. Each is at most
 * `LEAGUE_NAME_IDEA_MAX` (32) characters, inside what `CreateLeagueRequest`
 * takes. Added 2026-10-18.
 */
export const LeagueNameIdeasView = z.object({
  sport: z.string(),
  names: z.array(z.string()),
});
export type LeagueNameIdeasView = z.infer<typeof LeagueNameIdeasView>;

/**
 * What `POST /api/leagues/preview` answers: the rules these answers produce,
 * said three ways.
 *
 * `settings` is the object that would be stored, `doc` is the plain-English
 * rendering the wizard shows a step at a time, and `issues` is what
 * `validateLeagueSettings` says about it — the same three values the create
 * endpoint computes, from the same three functions, so what somebody read on
 * the last step is what the league gets.
 */
export const LeaguePreview = z.object({
  settings: LeagueSettings,
  doc: RulesDocument,
  issues: z.array(Issue),
  /** The scoring as the create form's table shows it (PLAN-2026-10-18 §2.2). Added 2026-10-18. */
  scoringTable: ScoringTable,
});
export type LeaguePreview = z.infer<typeof LeaguePreview>;

/**
 * One source the site's data comes from, and the sentence its terms ask a
 * site to show (`GET /api/credits`).
 */
export const CreditLine = z.object({
  /** The source as it names itself: `nflverse`. */
  source: z.string(),
  /** Its home page, for a link on its name. */
  homepage: z.string(),
  /** The sentence its license asks for, to be shown exactly as it is. */
  text: z.string(),
  /** The license by its own short name: `CC-BY-4.0`. */
  license: z.string(),
  /** Where the license is read in full, for a link on it. */
  licenseUrl: z.string(),
});
export type CreditLine = z.infer<typeof CreditLine>;

/**
 * `GET /api/credits` — where the site's data comes from (DIRECTION D1).
 *
 * Built from the source manifests (`@fsp/info`'s `attributionLines`), one line
 * for each distinct obligation, so a source added is credited without anybody
 * remembering to. Only what an adapter actually fetches is here: a source
 * cleared and never read is no part of what a person sees. In the site's
 * order, its main source first. Public, and the same for everybody until the
 * next deploy, so it is cached for an hour.
 */
export const CreditsView = z.object({
  lines: z.array(CreditLine),
});
export type CreditsView = z.infer<typeof CreditsView>;

/**
 * `GET /api/version` — which contract this deployment serves.
 *
 * `schema` is the same value as the `X-Api-Schema` header, and it is the
 * whole point: an installed client that stored the fingerprint it was built
 * against can ask once, compare, and say "update the app" instead of failing
 * a parse in front of somebody with no explanation (decision A10). Additive
 * changes do not move it — a field added to a view is not a break — so a
 * client that sees a different value is looking at a contract that removed or
 * renamed something, which is the case this project has promised not to
 * create and this is how it would be caught anyway.
 */
export const ApiVersion = z.object({
  schema: z.string(),
  /** The rule, in the response, so it does not live only in a document. */
  compatibility: z.literal('additive-only'),
});
export type ApiVersion = z.infer<typeof ApiVersion>;

/**
 * `GET /api/health` — whether the site's scheduled work is running, for a
 * watchdog outside Cloudflare (B-478, DIRECTION D11): an alarm the platform
 * raises cannot say that the platform is down, so something elsewhere asks.
 * Served `200` when everything named here is running; when anything has
 * stopped, the envelope's `503 unavailable` (reason `stopped`) saying which,
 * with this view in `details.health`. No answer at all is the third case,
 * and the watchdog's to notice.
 */
export const HealthView = z.object({
  ok: z.boolean(),
  /** The instant the answer was made. ISO. */
  checkedAt: z.string(),
  /** The ingest worker's minute sweep: locks, scoring, the clock's jobs, notices. */
  clock: z.object({
    /** When the latest sweep started; null when none ever has. ISO. */
    sweptAt: z.string().nullable(),
    stopped: z.boolean(),
  }),
  /** The ingest worker's passes, at least one an hour: stats, schedules, players, the late lines alarm. */
  ingest: z.object({
    /** When a pass last started a step (the clock's own rows aside); null when none ever has. ISO. */
    ranAt: z.string().nullable(),
    stopped: z.boolean(),
  }),
  /**
   * Whether a real game is within eight days either side of now. In the
   * season every file below is held to working; out of it a season's file
   * can have nothing to fetch, and only the passes running is asked.
   */
  inSeason: z.boolean(),
  /**
   * Each file the worker fetches on every pass (`source_resource`, a
   * `daily-rebuild` or `continuous` file): when a run of it last worked,
   * and whether it is failing — run since then, with no run that worked for
   * longer than twice its usual gap, in the season. A file the worker has
   * stopped running is the admin's Ingest page's *late*, not this.
   */
  files: z.array(
    z.object({
      sourceId: z.string(),
      family: z.string(),
      /** The newest run that worked (`ok`, `idle` or `partial`); null when none has. ISO. */
      workedAt: z.string().nullable(),
      failing: z.boolean(),
    }),
  ),
});
export type HealthView = z.infer<typeof HealthView>;

/** How a league's picks may be made: the pick orders and `auction`. One list, from the rules schema. */
export const DRAFT_TYPES: readonly string[] = DraftType.options;
/** Who may be present and enter the picks. One list, from the rules schema. */
export const DRAFT_MODES: readonly string[] = DraftMode.options;
